import { Loader2 } from 'lucide-react';
import type { CallbackLog } from '@/lib/types';

interface CallbackLogsSectionProps {
  callbackLogs: CallbackLog[] | undefined;
  isLoading: boolean;
  className?: string;
}

export default function CallbackLogsSection({ callbackLogs, isLoading, className = '' }: CallbackLogsSectionProps) {
  return (
    <div className={`space-y-3 ${className}`}>
      <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
        <span className="font-medium">Request payload:</span>{' '}
        <code className="text-primary/80">{'{'} job_id, status, timestamp, ...result {'}'}</code>
        <span className="ml-2">(see Result section below)</span>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading delivery logs...
        </div>
      ) : callbackLogs && callbackLogs.length > 0 ? (
        <div className="space-y-2">
          {callbackLogs.map((log) => {
            const isSuccess = log.http_status_code !== null && log.http_status_code >= 200 && log.http_status_code < 300;
            const isError = log.error_message || (log.http_status_code !== null && log.http_status_code >= 400);
            return (
              <div
                key={log.id}
                className={`p-3 rounded-lg border text-sm ${
                  isSuccess
                    ? 'bg-green-500/10 border-green-500/30'
                    : isError
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-muted/50 border-border'
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className={`font-medium ${isSuccess ? 'text-green-400' : isError ? 'text-red-400' : ''}`}>
                      Attempt {log.attempt_number}
                    </span>
                    {log.http_status_code !== null && (
                      <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                        isSuccess ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        HTTP {log.http_status_code}
                      </span>
                    )}
                    {log.duration_ms !== null && (
                      <span className="text-muted-foreground text-xs">
                        {log.duration_ms}ms
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">
                    {new Date(log.create_ts).toLocaleString()}
                  </span>
                </div>
                {log.error_message && (
                  <div className="mt-2 text-xs text-red-400 font-mono break-all">
                    <span className="font-medium">Error:</span> {log.error_message}
                  </div>
                )}
                {log.response_body && (
                  <div className="mt-2 text-xs font-mono break-all">
                    <span className="font-medium text-muted-foreground">Response:</span>{' '}
                    <span className={isSuccess ? 'text-green-400/80' : 'text-muted-foreground'}>
                      {log.response_body}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          No delivery attempts recorded yet.
        </div>
      )}
    </div>
  );
}
